/**
 * Registro único de módulos e ações (UI + gates).
 * Chaves curtas `jsonKey` reduzem o payload JSON em `cargo` (schema v2).
 */

export type PermissionModuleId =
  | "dashboard"
  | "pdv"
  | "delivery"
  | "pedidos"
  | "conversas"
  | "company"
  | "agendamentos"
  | "motoboys"
  | "manager"
  | "colaboradores"
  | "clientes"
  | "financeiro"
  | "configuracoes"
  | "license"
  | "delivai_controle";

/** Chaves de ação por módulo (ids estáveis no código). */
export type PermissionActionId =
  | "ver"
  | "aceitar"
  | "cancelar"
  | "editar"
  | "despachar"
  | "finalizar"
  | "ver_historico"
  | "pedido_interno"
  | "imprimir"
  | "enviar_mensagens"
  | "arquivar"
  | "editar_cardapio"
  | "editar_delivery"
  | "editar_dados_empresa"
  | "editar_prompts"
  | "editar_consumers"
  | "ver_banco"
  | "ver_logs"
  | "ver_observabilidade"
  | "criar"
  | "criar_editar"
  | "editar_permissoes"
  | "editar_contratos"
  | "publicar_contratos"
  | "excluir_versoes_contratos"
  | "editar_clientes";

export interface PermissionActionDef {
  id: PermissionActionId;
  /** Chave curta no JSON (ex.: ace, can). */
  jsonKey: string;
  labelPt: string;
}

export interface PermissionModuleDef {
  id: PermissionModuleId;
  jsonKey: string;
  labelPt: string;
  /** Rota da sidebar correspondente (path após /dashboard). */
  routeSegment: string;
  actions: PermissionActionDef[];
}

export const COLLABORATOR_PERMISSION_MODULES: PermissionModuleDef[] = [
  {
    id: "dashboard",
    jsonKey: "dash",
    labelPt: "Painel / dashboard",
    routeSegment: "orders",
    actions: [{ id: "ver", jsonKey: "v", labelPt: "Acessar painel" }],
  },
  {
    id: "pdv",
    jsonKey: "pdv",
    labelPt: "PDV (mesa e retirada)",
    routeSegment: "orders",
    actions: [
      { id: "ver", jsonKey: "v", labelPt: "Ver PDV" },
      { id: "aceitar", jsonKey: "a", labelPt: "Aceitar pedido" },
      { id: "cancelar", jsonKey: "c", labelPt: "Cancelar pedido" },
      { id: "editar", jsonKey: "e", labelPt: "Editar pedido" },
      { id: "finalizar", jsonKey: "f", labelPt: "Finalizar pedido" },
      { id: "ver_historico", jsonKey: "h", labelPt: "Ver histórico" },
      { id: "pedido_interno", jsonKey: "i", labelPt: "Colar / pedido interno" },
      { id: "imprimir", jsonKey: "p", labelPt: "Imprimir / comprovantes" },
    ],
  },
  {
    id: "delivery",
    jsonKey: "dlv",
    labelPt: "Delivery (despacho)",
    routeSegment: "delivery",
    actions: [
      { id: "ver", jsonKey: "v", labelPt: "Ver Delivery" },
      { id: "aceitar", jsonKey: "a", labelPt: "Aceitar pedido" },
      { id: "cancelar", jsonKey: "c", labelPt: "Cancelar pedido" },
      { id: "editar", jsonKey: "e", labelPt: "Editar pedido" },
      { id: "despachar", jsonKey: "d", labelPt: "Despachar / atribuir entregador" },
      { id: "finalizar", jsonKey: "f", labelPt: "Finalizar pedido" },
      { id: "ver_historico", jsonKey: "h", labelPt: "Ver histórico" },
      { id: "pedido_interno", jsonKey: "i", labelPt: "Fast Order / pedido interno" },
      { id: "imprimir", jsonKey: "p", labelPt: "Imprimir / comprovantes" },
    ],
  },
  {
    id: "pedidos",
    jsonKey: "ped",
    labelPt: "Gerenciamento de pedidos (legado)",
    routeSegment: "orders",
    actions: [
      { id: "ver", jsonKey: "v", labelPt: "Ver pedidos" },
      { id: "aceitar", jsonKey: "a", labelPt: "Aceitar pedido" },
      { id: "cancelar", jsonKey: "c", labelPt: "Cancelar pedido" },
      { id: "editar", jsonKey: "e", labelPt: "Editar pedido" },
      { id: "despachar", jsonKey: "d", labelPt: "Despachar / atribuir entregador" },
      { id: "finalizar", jsonKey: "f", labelPt: "Finalizar pedido" },
      { id: "ver_historico", jsonKey: "h", labelPt: "Ver histórico" },
      { id: "pedido_interno", jsonKey: "i", labelPt: "Pedido interno" },
      { id: "imprimir", jsonKey: "p", labelPt: "Imprimir / comprovantes" },
    ],
  },
  {
    id: "conversas",
    jsonKey: "conv",
    labelPt: "Conversas (WhatsApp)",
    routeSegment: "conversations",
    actions: [
      { id: "ver", jsonKey: "v", labelPt: "Ver conversas" },
      { id: "enviar_mensagens", jsonKey: "m", labelPt: "Enviar mensagens" },
      { id: "arquivar", jsonKey: "r", labelPt: "Arquivar / gerenciar" },
    ],
  },
  {
    id: "company",
    jsonKey: "comp",
    labelPt: "Cardápio / empresa",
    routeSegment: "company",
    actions: [
      { id: "ver", jsonKey: "v", labelPt: "Ver" },
      { id: "editar_cardapio", jsonKey: "ec", labelPt: "Editar cardápio" },
      { id: "editar_delivery", jsonKey: "ed", labelPt: "Editar delivery / bairros" },
      { id: "editar_dados_empresa", jsonKey: "ee", labelPt: "Editar dados da empresa" },
    ],
  },
  {
    id: "agendamentos",
    jsonKey: "agen",
    labelPt: "Agendamentos",
    routeSegment: "schedule",
    actions: [
      { id: "ver", jsonKey: "v", labelPt: "Ver" },
      { id: "criar", jsonKey: "cr", labelPt: "Criar" },
      { id: "editar", jsonKey: "e", labelPt: "Editar" },
      { id: "cancelar", jsonKey: "c", labelPt: "Cancelar" },
    ],
  },
  {
    id: "motoboys",
    jsonKey: "moto",
    labelPt: "Ranking Motoboys",
    routeSegment: "motoboys",
    actions: [
      { id: "ver", jsonKey: "v", labelPt: "Ver ranking" },
      { id: "editar", jsonKey: "e", labelPt: "Editar diárias / ranking" },
    ],
  },
  {
    id: "manager",
    jsonKey: "mgr",
    labelPt: "Gerente / resumo",
    routeSegment: "manager",
    actions: [{ id: "ver", jsonKey: "v", labelPt: "Ver" }],
  },
  {
    id: "colaboradores",
    jsonKey: "col",
    labelPt: "Colaboradores",
    routeSegment: "employees",
    actions: [
      { id: "ver", jsonKey: "v", labelPt: "Ver lista" },
      { id: "criar_editar", jsonKey: "ce", labelPt: "Criar e editar colaboradores" },
      { id: "editar_permissoes", jsonKey: "ep", labelPt: "Editar permissões de outros" },
    ],
  },
  {
    id: "clientes",
    jsonKey: "cli",
    labelPt: "Clientes",
    routeSegment: "clientes",
    actions: [
      { id: "ver", jsonKey: "v", labelPt: "Ver" },
      { id: "editar_clientes", jsonKey: "ev", labelPt: "Editar" },
    ],
  },
  {
    id: "financeiro",
    jsonKey: "fin",
    labelPt: "Financeiro",
    routeSegment: "manager",
    actions: [
      { id: "ver", jsonKey: "v", labelPt: "Ver" },
      { id: "editar", jsonKey: "e", labelPt: "Editar" },
    ],
  },
  {
    id: "configuracoes",
    jsonKey: "cfg",
    labelPt: "Configurações",
    routeSegment: "company",
    actions: [
      { id: "ver", jsonKey: "v", labelPt: "Ver" },
      { id: "editar", jsonKey: "e", labelPt: "Editar" },
    ],
  },
  {
    id: "license",
    jsonKey: "lic",
    labelPt: "Licença",
    routeSegment: "license",
    actions: [{ id: "ver", jsonKey: "v", labelPt: "Ver licença" }],
  },
  {
    id: "delivai_controle",
    jsonKey: "dva",
    labelPt: "Controle DelivAI (interno)",
    routeSegment: "delivai",
    actions: [
      { id: "ver", jsonKey: "v", labelPt: "Acessar painel interno" },
      { id: "editar", jsonKey: "e", labelPt: "Licenças — editar prazos, módulos e pay_types" },
      { id: "criar", jsonKey: "cr", labelPt: "Evolution — criar e gerir instâncias" },
      { id: "editar_cardapio", jsonKey: "ec", labelPt: "Empresas — editar registro companies" },
      { id: "editar_prompts", jsonKey: "pr", labelPt: "Prompts — editar templates do bot" },
      { id: "editar_consumers", jsonKey: "co", labelPt: "Clientes — listar e mesclar consumers" },
      { id: "ver_banco", jsonKey: "db", labelPt: "Banco — studio interno (tabelas e SQL)" },
      { id: "ver_logs", jsonKey: "lg", labelPt: "Logs — executions n8n em tempo real" },
      {
        id: "ver_observabilidade",
        jsonKey: "ob",
        labelPt: "Observabilidade — métricas (Prometheus) e Loki",
      },
      { id: "editar_permissoes", jsonKey: "ep", labelPt: "Permissões — gerir outros desenvolvedores" },
      {
        id: "editar_contratos",
        jsonKey: "ct",
        labelPt: "Contratos — revisar/editar minutas (B2B, consumidor, colaboradores, equipe interna, advogado)",
      },
      {
        id: "publicar_contratos",
        jsonKey: "cp",
        labelPt: "Contratos — ativar versão oficial (somente master)",
      },
      {
        id: "excluir_versoes_contratos",
        jsonKey: "cx",
        labelPt: "Contratos — excluir versões (somente master; vai para lixeira)",
      },
    ],
  },
];

export function getModuleById(id: PermissionModuleId): PermissionModuleDef | undefined {
  return COLLABORATOR_PERMISSION_MODULES.find((m) => m.id === id);
}

export function getModuleByJsonKey(short: string): PermissionModuleDef | undefined {
  return COLLABORATOR_PERMISSION_MODULES.find((m) => m.jsonKey === short);
}

export function getModuleByRouteSegment(segment: string): PermissionModuleDef | undefined {
  return COLLABORATOR_PERMISSION_MODULES.find((m) => m.routeSegment === segment);
}
